import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Collaborative variant of MarkdownEditor (experimental, behind the
// `experimentalCollab` preference).
//
// Unlike MarkdownEditor — a *controlled* component where React owns `value` —
// here the Yjs `Y.Text` owns the document. We therefore build a raw CodeMirror
// `EditorView` and let `yCollab` drive it; React only mounts the view and
// forwards the materialized text out via `onChange` so existing autosave keeps
// persisting. Theme / direction / read-only are swapped through Compartments so
// prop changes never tear down the CRDT document (which would drop the caret
// and, once a transport is attached, the shared session).
//
// Slice 1: the session is local-only (see readmeCollabClient). The component is
// already transport-agnostic — a provider attached to the same Y.Doc needs no
// change here.
import { useEffect, useMemo, useRef } from "react";
import { EditorState, Compartment } from "@codemirror/state";
import { EditorView, keymap } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { markdown } from "@codemirror/lang-markdown";
import { languages } from "@codemirror/language-data";
import { githubLight, githubDark } from "@uiw/codemirror-theme-github";
import TurndownService from "turndown";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore -- no @types package
import { gfm as turndownGfm } from "@joplin/turndown-plugin-gfm";
import { MarkdownEditorToolbar } from "./MarkdownEditorToolbar";
import { createReadmeCollabSession, } from "@/collab/readmeCollabClient";
const LARGE_PASTE_CHARS = 500;
const turndownService = new TurndownService({
    headingStyle: "atx",
    codeBlockStyle: "fenced",
    bulletListMarker: "-",
    emDelimiter: "*",
    hr: "---",
});
turndownService.use(turndownGfm);
const RICH_HTML_RE = /<(?:p|h[1-6]|ul|ol|li|table|thead|tbody|tr|td|th|pre|code|blockquote|div|article|section|figure|figcaption|hr|br|strong|b|em|i|u|s|del|ins|a|img)\b/i;
function htmlLooksRich(html) {
    return RICH_HTML_RE.test(html);
}
function toPx(value) {
    if (value === undefined)
        return undefined;
    return typeof value === "number" ? `${value}px` : value;
}
function baseTheme() {
    return EditorView.theme({
        "&": { backgroundColor: "transparent", fontSize: "14.5px" },
        ".cm-content": {
            fontFamily: '"JetBrains Mono", ui-monospace, monospace',
            padding: "12px 14px",
            caretColor: "currentColor",
        },
        ".cm-line": { unicodeBidi: "plaintext", lineHeight: "1.6" },
        "&.cm-focused": { outline: "none" },
        ".cm-scroller": {
            fontFamily: '"JetBrains Mono", ui-monospace, monospace',
            overflowAnchor: "none",
        },
        ".cm-activeLine": { backgroundColor: "rgba(148, 163, 184, 0.08)" },
    });
}
export function CollaborativeMarkdownEditor({ value, onChange, onBlur, onLargePaste, user, docKey, dir, minHeight, height, autoFocus, readOnly, colorMode = "light", hideToolbar, className, }) {
    const hostRef = useRef(null);
    const viewRef = useRef(null);
    const sessionRef = useRef(null);
    // ReactCodeMirrorRef-compatible handle for the shared toolbar.
    const toolbarRef = useRef(null);
    // Latest-callback refs so the long-lived EditorView never holds a stale
    // closure (the view is created once per docKey, callbacks can change often).
    const onChangeRef = useRef(onChange);
    const onBlurRef = useRef(onBlur);
    const onLargePasteRef = useRef(onLargePaste);
    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
    useEffect(() => { onBlurRef.current = onBlur; }, [onBlur]);
    useEffect(() => { onLargePasteRef.current = onLargePaste; }, [onLargePaste]);
    // Seed text + user identity captured once per session (per docKey). Using
    // refs avoids re-seeding when the parent re-renders with the same document.
    const initialTextRef = useRef(value);
    const userRef = useRef(user);
    const themeCompartment = useMemo(() => new Compartment(), []);
    const dirCompartment = useMemo(() => new Compartment(), []);
    const readOnlyCompartment = useMemo(() => new Compartment(), []);
    // Build the editor + CRDT session once per document. Symmetric create/destroy
    // makes this React-StrictMode safe (the dev double-invoke tears the first
    // instance down before creating the second).
    useEffect(() => {
        const host = hostRef.current;
        if (!host)
            return;
        const session = createReadmeCollabSession({
            initialText: initialTextRef.current,
            user: userRef.current,
            room: docKey,
        });
        sessionRef.current = session;
        const pasteHandler = EditorView.domEventHandlers({
            paste(event, view) {
                const currentContent = view.state.doc.toString();
                const html = event.clipboardData?.getData("text/html");
                if (html && html.trim() && htmlLooksRich(html)) {
                    let md;
                    try {
                        md = turndownService.turndown(html);
                    }
                    catch {
                        return false;
                    }
                    if (!md || !md.trim())
                        return false;
                    if (md.length >= LARGE_PASTE_CHARS) {
                        onLargePasteRef.current?.(currentContent);
                    }
                    event.preventDefault();
                    view.dispatch({
                        ...view.state.replaceSelection(md),
                        scrollIntoView: true,
                        userEvent: "input.paste",
                    });
                    return true;
                }
                const text = event.clipboardData?.getData("text/plain") ?? "";
                if (text.length >= LARGE_PASTE_CHARS) {
                    onLargePasteRef.current?.(currentContent);
                }
                return false;
            },
            blur() {
                onBlurRef.current?.();
                return false;
            },
        });
        const updateListener = EditorView.updateListener.of((u) => {
            if (u.docChanged) {
                onChangeRef.current(u.state.doc.toString());
            }
        });
        const state = EditorState.create({
            doc: session.getText(),
            extensions: [
                // history() is included for non-collab key handling; yCollab supplies
                // its own Yjs-aware undo manager which takes precedence for the doc.
                history(),
                keymap.of([...defaultKeymap, ...historyKeymap]),
                markdown({ codeLanguages: languages, addKeymap: true }),
                EditorView.lineWrapping,
                baseTheme(),
                dirCompartment.of(EditorView.contentAttributes.of({ dir: dir === "rtl" ? "rtl" : "auto" })),
                themeCompartment.of(colorMode === "dark" ? githubDark : githubLight),
                readOnlyCompartment.of([
                    EditorState.readOnly.of(!!readOnly),
                    EditorView.editable.of(!readOnly),
                ]),
                pasteHandler,
                updateListener,
                // The Yjs binding must come last so its facets win over defaults.
                session.extension,
            ],
        });
        const view = new EditorView({ state, parent: host });
        viewRef.current = view;
        toolbarRef.current = { view, state: view.state, editor: host };
        if (autoFocus)
            view.focus();
        return () => {
            view.destroy();
            session.destroy();
            viewRef.current = null;
            sessionRef.current = null;
            toolbarRef.current = null;
        };
        // Rebuild only when the document identity changes. Theme/dir/readOnly are
        // reconfigured via compartments below, not by rebuilding.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docKey]);
    // Reconfigure direction without rebuilding the document.
    useEffect(() => {
        viewRef.current?.dispatch({
            effects: dirCompartment.reconfigure(EditorView.contentAttributes.of({ dir: dir === "rtl" ? "rtl" : "auto" })),
        });
    }, [dir, dirCompartment]);
    // Reconfigure theme without rebuilding the document.
    useEffect(() => {
        viewRef.current?.dispatch({
            effects: themeCompartment.reconfigure(colorMode === "dark" ? githubDark : githubLight),
        });
    }, [colorMode, themeCompartment]);
    // Reconfigure read-only without rebuilding the document.
    useEffect(() => {
        viewRef.current?.dispatch({
            effects: readOnlyCompartment.reconfigure([
                EditorState.readOnly.of(!!readOnly),
                EditorView.editable.of(!readOnly),
            ]),
        });
    }, [readOnly, readOnlyCompartment]);
    // Reconcile an EXTERNAL change to `value` (e.g. revision restore) into the
    // CRDT document. Edits the user makes here flow out via onChange and come
    // back as an identical `value`, so this is a no-op for normal typing.
    useEffect(() => {
        const session = sessionRef.current;
        if (!session)
            return;
        // When connected to the Worker, the shared document is authoritative —
        // reconciling the `value` prop would duplicate content against the synced
        // server state. (Restoring a revision while connected is handled as a
        // broadcast CRDT transaction in a later slice.)
        if (session.isRemote)
            return;
        if (session.getText() !== value) {
            session.setText(value);
        }
    }, [value]);
    return (_jsxs("div", { className: `md-editor-shell flex flex-col ${className ?? ""}`, children: [!hideToolbar && !readOnly && _jsx(MarkdownEditorToolbar, { viewRef: toolbarRef }), _jsx("div", { ref: hostRef, className: "cm-collab-host overflow-auto", style: { height: toPx(height), minHeight: toPx(minHeight) } })] }));
}
